import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import Seo from '@/components/Seo'
import { useAuth } from '@/hooks/useAuth'
import { contactStatusLabel, fetchAllContactMessages, markContactMessageRead, type ContactMessage } from '@/lib/contact'

function AdminMessages() {
  const { isAuthenticated, isAdmin } = useAuth()
  const [rows, setRows] = useState<ContactMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const list = await fetchAllContactMessages()
      setRows(list)
    } catch (err) {
      console.error('No se pudieron cargar los mensajes:', err)
      setError('No se pudieron cargar los mensajes. Inténtalo de nuevo.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAdmin) void load()
  }, [isAdmin, load])

  if (!isAuthenticated) {
    return <Navigate to="/acceso?next=/admin/mensajes" replace />
  }

  if (!isAdmin) {
    return <Navigate to="/panel" replace />
  }

  const markRead = async (row: ContactMessage) => {
    if (!row._id || row.status === 'read') return
    setBusyId(row._id)
    setError('')
    try {
      await markContactMessageRead(row._id)
      await load()
    } catch (err) {
      console.error('No se pudo marcar el mensaje como leído:', err)
      setError('No se pudo actualizar el mensaje.')
    } finally {
      setBusyId('')
    }
  }

  return (
    <>
      <Seo title="Mensajes de contacto" description="Revisa los mensajes enviados desde el formulario de contacto de HFCursos." />
      <section className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <p className="text-xs uppercase tracking-[0.28em] text-[#C41E3A]">Gestión</p>
        <h1 className="mt-3 font-['Fraunces'] text-4xl">Mensajes de contacto</h1>
        <p className="mt-3 max-w-2xl text-sm text-[#1E3A5F]/70">
          Aquí llegan los mensajes enviados desde la página de Contacto.
        </p>

        {error ? <p className="mt-4 text-sm text-[#C41E3A]">{error}</p> : null}

        {loading ? (
          <p className="mt-10 text-sm text-[#1E3A5F]/60">Cargando mensajes…</p>
        ) : rows.length === 0 ? (
          <p className="mt-10 border border-dashed border-[#1E3A5F]/20 p-8 text-sm text-[#1E3A5F]/65">
            Todavía no hay mensajes de contacto.
          </p>
        ) : (
          <ul className="mt-8 space-y-4">
            {rows.map((row) => (
              <li key={row._id} className="border border-[#1E3A5F]/10 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#C41E3A]">{contactStatusLabel(row.status)}</p>
                    <h2 className="mt-1 font-['Fraunces'] text-2xl">{row.subject || 'Sin asunto'}</h2>
                    <p className="mt-2 text-sm">{row.name}</p>
                    <p className="text-sm text-[#1E3A5F]/60">
                      <a className="underline" href={`mailto:${row.email}`}>
                        {row.email}
                      </a>
                    </p>
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-[#1E3A5F]/80">{row.message}</p>
                    <p className="mt-3 text-xs text-[#1E3A5F]/45">
                      {new Date(row.createdAt).toLocaleString('es-MX')}
                    </p>
                  </div>
                </div>
                {row.status !== 'read' ? (
                  <div className="mt-4">
                    <button
                      type="button"
                      disabled={busyId === row._id}
                      onClick={() => markRead(row)}
                      className="border border-[#1E3A5F] px-3 py-2 text-sm disabled:opacity-50"
                    >
                      Marcar como leída
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}

export default AdminMessages
